/**
 * Number and money formatting for tables, tiles and charts. Money is always
 * integer cents in Velora contracts; formatting never does float maths on
 * dollars.
 */

const moneyFormatters = new Map<string, Intl.NumberFormat>();

function moneyFormatter(currency: string, compact: boolean, cents: boolean): Intl.NumberFormat {
  const key = `${currency}|${compact}|${cents}`;
  let f = moneyFormatters.get(key);
  if (!f) {
    f = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      ...(compact
        ? { notation: 'compact', maximumFractionDigits: 1 }
        : { minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0 }),
    });
    moneyFormatters.set(key, f);
  }
  return f;
}

export interface MoneyFormatOptions {
  currency?: string;
  /** $12.4K style for tiles and axis labels. */
  compact?: boolean;
  /** Show cents (default true; false rounds to whole dollars). */
  showCents?: boolean;
  /** Accounting style: negatives in parentheses, as on ledgers and statements. */
  accounting?: boolean;
  /** Prefix positives with + (for deltas). */
  signed?: boolean;
}

export function formatMoney(cents: number, options: MoneyFormatOptions = {}): string {
  const {
    currency = 'USD',
    compact = false,
    showCents = true,
    accounting = false,
    signed = false,
  } = options;
  if (!Number.isFinite(cents)) return '';
  const abs = Math.abs(Math.round(cents)) / 100;
  const body = moneyFormatter(currency, compact, showCents).format(abs);
  if (cents < 0 && Math.round(cents) !== 0) return accounting ? `(${body})` : `-${body}`;
  if (signed && cents > 0) return `+${body}`;
  return body;
}

const intFormat = new Intl.NumberFormat('en-US');
const compactFormat = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

export function formatNumber(n: number, compact = false): string {
  if (!Number.isFinite(n)) return '';
  return compact ? compactFormat.format(n) : intFormat.format(n);
}

/** 0.4567 -> "45.7%". */
export function formatPercent(fraction: number, digits = 1): string {
  if (!Number.isFinite(fraction)) return '';
  return `${(fraction * 100).toFixed(digits).replace(/\.0+$/, '')}%`;
}

/**
 * A stored phone number for display: North American E.164 ("+19895550100")
 * becomes "(989) 555-0100"; anything else is returned as stored.
 */
export function formatPhone(value: string): string {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(value.trim());
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : value;
}

/** Relative change between two values: (cur - prev) / |prev|; null when prev is 0. */
export function relativeChange(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return (current - previous) / Math.abs(previous);
}
